import React, { useState, useEffect, useMemo } from "react";
import { Button, Collapse } from "@mui/material";
import BasicTable from "../../Components/Table/Material";
import { usePagination } from "../../Hooks/usePagination";
import Common from "../../Components/Layouts/Common";
import { useModal } from "../../Hooks/useModal";
import Modal from "../../Components/Modal";
import IMISDataInsertionModal from "../../Components/Modal/IMISDataInsertionModal";
import useWindowWidth from "../../Hooks/useWindowWidth";
import useApi from "../../Hooks/useApi";
import { fetchIMISCityData } from "../../Utils/imisCityDashboardUtils";
import TextField from "../../Components/UI/Inputs/TextField";
import DropDown from "../../Components/UI/Inputs/DropDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faXmark, faFilter } from "@fortawesome/free-solid-svg-icons";
import { sortRows, nextSortConfig } from "../../Helpers/tableSort";

// Build dropdown options from whatever distinct values are actually present
// in the loaded data, since there's no documented enum for these fields.
const buildOptionsFromValues = (rows, key) => {
  const distinct = Array.from(
    new Set((rows || []).map((row) => row[key]).filter((v) => v || v === 0))
  );
  return [
    { name: "All", value: "" },
    ...distinct.map((value) => ({ name: String(value), value })),
  ];
};

const IMISDashboardManagment = () => {
  const paginateProps = usePagination();
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});
  const { isOpen, closeModal, openModal } = useModal();

  const { request } = useApi();
  const windowWidth = useWindowWidth();

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const yearOptions = useMemo(() => buildOptionsFromValues(data, "year"), [data]);
  const statusOptions = useMemo(
    () => buildOptionsFromValues(data, "status"),
    [data]
  );

  const handleSort = (key) => {
    setSortConfig((prev) => nextSortConfig(prev, key));
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setYearFilter("");
    setStatusFilter("");
    paginateProps.setPage(0);
  };

  // Full dataset is loaded in one shot (no server-side search/filter/sort/
  // pagination params exist for this endpoint), so this all runs client-side.
  const filteredSortedData = useMemo(() => {
    let rows = data || [];

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      rows = rows.filter((row) => row.city?.toLowerCase().includes(term));
    }
    if (yearFilter) {
      rows = rows.filter((row) => String(row.year) === String(yearFilter));
    }
    if (statusFilter) {
      rows = rows.filter((row) => row.status === statusFilter);
    }

    return sortRows(rows, sortConfig);
  }, [data, searchTerm, yearFilter, statusFilter, sortConfig]);

  useEffect(() => {
    paginateProps.setTotalData(filteredSortedData.length);
  }, [filteredSortedData]);

  useEffect(() => {
    paginateProps.setPage(0);
  }, [searchTerm, yearFilter, statusFilter]);

  const paginatedData = useMemo(() => {
    const start = paginateProps.page * paginateProps.rowsPerPage;
    return filteredSortedData.slice(start, start + paginateProps.rowsPerPage);
  }, [filteredSortedData, paginateProps.page, paginateProps.rowsPerPage]);

  const handleView = (row) => {
    openModal();
    setSelectedRow({ ...row, year: row.year, status: row.status });
  };

  const columns = [
    {
      id: "city",
      label: "City",
      minWidth: 100,
      sortable: true,
    },
    {
      id: "year",
      label: "Year",
      minWidth: 100,
      sortable: true,
    },
    {
      id: "uploader",
      label: "Uploader",
      minWidth: 170,
      sortable: true,
    },
    {
      id: "uploaderDate",
      label: "Uploaded Date",
      minWidth: 100,
      sortable: true,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      sortable: true,
    },

    {
      id: "action",
      label: "Action",
      minWidth: 170,
      align:"center",
      renderCell: (row, rowIndex) => {
        return (
      <div className="flex justify-center">
  <button
    onClick={(e) => handleView(row)}
    className={`bg-[#1976d2] border border-gray-300 p-2 px-8 text-white rounded-md flex items-center justify-center cursor-pointer `}
  >
    View {row.status !== "PUBLISHED" ? "and Publish" : ""}
  </button>
</div>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchCityYearListData = async () => {
      try {
        await fetchIMISCityData(request, setData);
      } catch (error) {
        console.log(error);
      }
    };

    fetchCityYearListData();
  }, [request]);

  return (
    <Common headline={"IMIS Dashboard"}>
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
        <Button
          variant="outlined"
          startIcon={<FontAwesomeIcon icon={faFilter} />}
          onClick={() => setIsFilterVisible((prev) => !prev)}
        >
          {isFilterVisible ? "Hide Filters" : "Show Filters"}
        </Button>
        {(searchTerm || yearFilter || statusFilter) && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<FontAwesomeIcon icon={faXmark} />}
            onClick={handleResetFilters}
          >
            Reset Filters
          </Button>
        )}
      </div>

      <Collapse in={isFilterVisible}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pb-4">
          <TextField
            value={searchTerm}
            name="searchTerm"
            onValueChange={(_, value) => setSearchTerm(value)}
            icon={<FontAwesomeIcon icon={faSearch} />}
            placeholder="Search by city"
            fullWidth
          />
          <DropDown
            setter={setYearFilter}
            placeholder="Select Year"
            value={yearFilter}
            options={yearOptions}
            fullWidth
          />
          <DropDown
            setter={setStatusFilter}
            placeholder="Select Status"
            value={statusFilter}
            options={statusOptions}
            fullWidth
          />
        </div>
      </Collapse>

      <BasicTable
        columns={columns}
        data={paginatedData}
        paginateProps={paginateProps}
        sortConfig={sortConfig}
        onSort={handleSort}
        maxHeight={15000}
      />

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        style={{ width: windowWidth >= 768 ? "70vw" : "90vw" }}
      >
        <IMISDataInsertionModal imis={selectedRow} onClose={closeModal} />
      </Modal>
    </Common>
  );
};

export default IMISDashboardManagment;
